import { layerLabel } from '@/utils/layerLabel';
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Pentagon, Minus, Loader2 } from 'lucide-react';
import { DraggablePanel } from '../common/DraggablePanel';
import { useLayerStore } from '../../store/layer.store';
import { useMapStore } from '../../store/map.store';
import { useToast } from '../common/Toast';
import apiClient from '../../services/api';
import { EditFeatureDialog, ColumnInfo } from './EditFeatureDialog';
import { GeoJSON } from 'ol/format';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
import ImageLayer from 'ol/layer/Image';
import ImageWMS from 'ol/source/ImageWMS';
import { geoserverEndpoint } from '@/utils/geoserverAccess';

/**
 * Supported geometry types for the unified add-feature panel.
 * Each type maps to:
 *   - a geometry type filter string (matched against PostGIS geometry_columns)
 *   - a panel title, icon, and instruction text
 */
export type FeatureGeometryType = 'Point' | 'Polygon' | 'LineString';

interface AddFeaturePanelProps {
  geometryType: FeatureGeometryType;
  onClose: () => void;
  selectedPoint?: [number, number] | null; // for Point type: [lon, lat] in EPSG:4326
  drawnFeature: Feature<Geometry> | null;
}

/** Config per geometry type */
const GEOM_CONFIG: Record<FeatureGeometryType, {
  title: string;
  icon: React.ReactNode;
  filterKey: string;
  drawInstruction: string;
  successMessage: string;
}> = {
  Point: {
    title: 'tools.add_point',
    icon: <MapPin size={14} />,
    filterKey: 'POINT',
    drawInstruction: 'feature.draw_point',
    successMessage: 'feature.added_point',
  },
  Polygon: {
    title: 'tools.add_polygon',
    icon: <Pentagon size={14} />,
    filterKey: 'POLYGON',
    drawInstruction: 'feature.draw_polygon',
    successMessage: 'feature.added_polygon',
  },
  LineString: {
    title: 'tools.add_line',
    icon: <Minus size={14} />,
    filterKey: 'LINE',
    drawInstruction: 'feature.draw_line',
    successMessage: 'feature.added_line',
  },
};

export const AddFeaturePanel: React.FC<AddFeaturePanelProps> = ({
  geometryType,
  onClose,
  selectedPoint,
  drawnFeature,
}) => {
  const { t } = useTranslation();
  const config = GEOM_CONFIG[geometryType];
  const allLayers = useLayerStore((s) => s.allLayers);
  const setLayerVisible = useLayerStore((s) => s.setLayerVisible);
  const { addToast } = useToast();
  const [selectedLayer, setSelectedLayer] = useState('');
  const [columns, setColumns] = useState<ColumnInfo[]>([]);
  const [loadingCols, setLoadingCols] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [geometry, setGeometry] = useState<any>(null);
  const [geomTypes, setGeomTypes] = useState<Record<string, string>>({});
  const [loadingGeomTypes, setLoadingGeomTypes] = useState(true);

  /* Fetch geometry types for all layers on mount — uses new endpoint */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiClient.get('/features/geometry-types');
        if (!cancelled) setGeomTypes(resp.data || {});
      } catch (err) {
        console.error('Failed to fetch geometry types:', err);
      } finally {
        if (!cancelled) setLoadingGeomTypes(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* Filter layers by geometry type */
  const layerOptions = allLayers
    .filter((l) => {
      if (!l.layerName) return false;
      const gType = (geomTypes[l.layerName] || '').toUpperCase();
      return gType.includes(config.filterKey);
    })
    .map((l) => ({ value: l.layerName, label: layerLabel(l) }));

  /** When user picks a layer, make it visible on the map */
  const handleLayerSelect = useCallback((layerName: string) => {
    setSelectedLayer(layerName);
    if (!layerName) return;
    const layer = allLayers.find((l) => l.layerName === layerName);
    if (!layer) return;

    setLayerVisible(layer.id, true);

    const mapInst = useMapStore.getState().mapInstance;
    if (!mapInst) return;

    let found = false;
    mapInst.getLayers().forEach((olLayer: any) => {
      if (olLayer.get('name') === layerName || olLayer.get('name') === (layer.displayName || layerName)) {
        olLayer.setVisible(true);
        found = true;
      }
    });

    if (!found) {
      const ws = layer.layerWorkspace?.name || 'cite';
      const olLayer = new ImageLayer({
        source: new ImageWMS({
          url: geoserverEndpoint(ws, 'wms'),
          params: { LAYERS: `${ws}:${layerName}` },
          serverType: 'geoserver',
          crossOrigin: 'anonymous',
          ratio: 1,
        }),
        properties: { name: layer.displayName || layerName, layerName, layerId: layer.id },
        visible: true,
        zIndex: 73,
      });
      mapInst.addLayer(olLayer);
    }
  }, [allLayers, setLayerVisible]);

  /* Convert drawn feature to GeoJSON when available */
  useEffect(() => {
    if (!drawnFeature || !selectedLayer) return;

    const geoJson = new GeoJSON();
    const geojsonObj = geoJson.writeFeatureObject(drawnFeature, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857',
    });

    let geom = geojsonObj.geometry;

    // For polygons, wrap in Multi to match PostGIS MultiPolygon columns
    if (geom.type === 'Polygon' && geometryType === 'Polygon') {
      geom = { type: 'MultiPolygon', coordinates: [geom.coordinates] };
    }

    setGeometry(geom);
    fetchColumns(selectedLayer);
  }, [drawnFeature, selectedLayer, geometryType]);

  const fetchColumns = useCallback(async (tableName: string) => {
    setLoadingCols(true);
    try {
      const resp = await apiClient.get(`/features/${tableName}/columns`);
      const data = resp.data;
      const colTypes: Record<string, string> = data.ColumnTypes || {};
      const colOrders: Record<string, number> = data.ColumnOrders || {};
      const cols: ColumnInfo[] = Object.entries(colTypes).map(([name, type]) => ({
        name,
        type: type as string,
        ordinal: colOrders[name] ?? 999,
      }));
      setColumns(cols);
      setShowEditDialog(true);
    } catch (err: any) {
      console.error('Failed to fetch columns:', err);
      addToast(t('feature.columns_failed', { reason: err?.message || t('errors.unknown') }), 'error');
    } finally {
      setLoadingCols(false);
    }
  }, [addToast]);

  const handleSave = useCallback(async (values: Record<string, any>) => {
    if (!geometry || !selectedLayer) return;
    setSaving(true);
    try {
      const tableInfo: Record<string, string> = {};
      columns.forEach((col) => { tableInfo[col.name] = col.type; });

      // Find the actual geometry column name from table columns
      const GEOM_NAMES = ['geom', 'the_geom', 'wkb_geometry', 'geometry'];
      const geomCol = columns.find((c) => c.type === 'geometry')?.name
        || columns.find((c) => GEOM_NAMES.includes(c.name.toLowerCase()))?.name || 'geom';

      // Clean values: convert empty strings to null
      const cleanedValues: Record<string, any> = {};
      for (const [k, v] of Object.entries(values)) {
        if (v === '' || v === undefined) {
          cleanedValues[k] = null;
        } else {
          cleanedValues[k] = v;
        }
      }

      const feature = { ...cleanedValues, [geomCol]: JSON.stringify(geometry) };

      await apiClient.post(`/features/${selectedLayer}`, {
        feature,
        tableInfo,
      });

      setShowEditDialog(false);

      // Refresh the layer on the map
      const mapInst = useMapStore.getState().mapInstance;
      if (mapInst) {
        const displayName = allLayers.find((l) => l.layerName === selectedLayer)?.displayName || selectedLayer;
        mapInst.getLayers().forEach((layer: any) => {
          const name = layer.get('name');
          if (name === selectedLayer || name === displayName) {
            const src = layer.getSource?.();
            if (src?.updateParams) src.updateParams({ _t: Date.now() });
          }
        });
      }

      addToast(t(config.successMessage), 'success');
    } catch (err: any) {
      console.error('Save failed:', err);
      addToast(
        t('feature.save_failed', { reason: err?.response?.data?.message || err?.message || t('errors.unknown') }),
        'error',
        8000,
      );
    } finally {
      setSaving(false);
    }
  }, [geometry, selectedLayer, columns, allLayers, addToast, config.successMessage, geometryType, t]);

  const hasDrawn = geometryType === 'Point' ? !!selectedPoint : !!drawnFeature;

  return (
    <>
      <DraggablePanel
        title={t(config.title)}
        onClose={onClose}
        icon={config.icon}
        width={300}
      >
        <div className="p-3 space-y-3">
          {/* Layer selector */}
          <div>
            <span className="text-[10px] text-gn-secondary font-medium block mb-1">{t('feature.destination')}</span>
            {loadingGeomTypes ? (
              <div className="flex items-center gap-2 text-xs text-gn-blue py-1.5">
                <Loader2 size={14} className="animate-spin" />
                {t('feature.loading_layers')}
              </div>
            ) : (
              <select
                value={selectedLayer}
                onChange={(e) => handleLayerSelect(e.target.value)}
                className="w-full px-2 py-1.5 text-xs text-gn-primary bg-gn-tertiary border border-gn rounded focus:border-gn-blue focus:outline-none"
              >
                <option value="">{t('feature.select_layer')}</option>
                {layerOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            )}
          </div>

          {!selectedLayer && (
            <div className="text-[11px] text-gn-warning bg-gn-warning-light border border-gn rounded px-2 py-1.5">
              1. {t('feature.step_select')}
            </div>
          )}

          {selectedLayer && !hasDrawn && (
            <div className="text-[11px] text-gn-warning bg-gn-warning-light border border-gn rounded px-2 py-1.5 flex items-center gap-1.5">
              {config.icon}
              2. {t(config.drawInstruction)}
            </div>
          )}

          {geometryType === 'Point' && selectedPoint && (
            <div className="text-[11px] text-gn-success bg-gn-success-light border border-gn rounded px-2 py-1.5">
              {t('feature.point_at', { lat: selectedPoint[1].toFixed(5), lon: selectedPoint[0].toFixed(5) })}
            </div>
          )}

          {geometryType !== 'Point' && drawnFeature && (
            <div className="text-[11px] text-gn-success bg-gn-success-light border border-gn rounded px-2 py-1.5">
              {t(geometryType === 'Polygon' ? 'feature.drawn_polygon' : 'feature.drawn_line')}
            </div>
          )}

          {loadingCols && (
            <div className="flex items-center gap-2 text-xs text-gn-blue">
              <Loader2 size={14} className="animate-spin" />
              {t('feature.loading_columns')}
            </div>
          )}
        </div>
      </DraggablePanel>

      {/* Edit dialog */}
      <EditFeatureDialog
        open={showEditDialog}
        title={t(config.title)}
        columns={columns}
        geometry={geometry}
        tableName={selectedLayer}
        onSave={handleSave}
        onClose={() => setShowEditDialog(false)}
        saving={saving}
      />
    </>
  );
};
